const TRANSLIT: Record<string, string> = {
  а: "a",
  б: "b",
  в: "v",
  г: "g",
  д: "d",
  е: "e",
  ё: "yo",
  ж: "zh",
  з: "z",
  и: "i",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "kh",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "shch",
  ъ: "",
  ы: "y",
  ь: "",
  э: "e",
  ю: "yu",
  я: "ya",
};

export type TocHeading = {
  depth: 2 | 3;
  text: string;
  id: string;
};

function transliterate(text: string): string {
  return text
    .toLowerCase()
    .split("")
    .map((char) => TRANSLIT[char] ?? char)
    .join("");
}

function stripMarkdown(text: string): string {
  return text
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, "")
    .trim();
}

export function createHeadingId(text: string, seen?: Map<string, number>): string {
  const baseId =
    transliterate(stripMarkdown(text))
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/-{2,}/g, "-") || "section";

  if (!seen) {
    return baseId;
  }

  const count = seen.get(baseId) ?? 0;
  seen.set(baseId, count + 1);

  if (count === 0) {
    return baseId;
  }

  return `${baseId}-${count + 1}`;
}

export function extractTocHeadings(markdown: string): TocHeading[] {
  const headings: TocHeading[] = [];
  const seen = new Map<string, number>();
  let activeFence: "`" | "~" | null = null;
  const lines = markdown.split("\n");

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const fenceMatch = line.match(/^ {0,3}(`{3,}|~{3,})/);
    if (fenceMatch) {
      const marker = fenceMatch[1][0] as "`" | "~";

      if (activeFence === marker) {
        activeFence = null;
      } else if (activeFence === null) {
        activeFence = marker;
      }

      continue;
    }

    if (activeFence !== null) {
      continue;
    }

    const nextLine = lines[index + 1];
    const setextMatch = nextLine?.match(/^ {0,3}(=+|-+)\s*$/);
    const setextText = stripMarkdown(line);

    if (setextMatch && setextText) {
      if (setextMatch[1][0] === "-") {
        headings.push({
          depth: 2,
          text: setextText,
          id: createHeadingId(setextText, seen),
        });
      }

      index += 1;
      continue;
    }

    const match = line.match(/^ {0,3}(#{2,3})\s+(.+?)\s*#*\s*$/);
    if (!match) {
      continue;
    }

    const depth = match[1].length as 2 | 3;
    const text = stripMarkdown(match[2]);
    if (!text) {
      continue;
    }

    headings.push({
      depth,
      text,
      id: createHeadingId(text, seen),
    });
  }

  return headings;
}
